import de from 'messages/de.json'
import enGb from 'messages/en-GB.json'
import en from 'messages/en.json'
import es from 'messages/es.json'
import fr from 'messages/fr.json'
import ja from 'messages/ja.json'
import ko from 'messages/ko.json'
import zhTw from 'messages/zh-TW.json'
import { ReactNode, useMemo } from 'react'
import { IntlProvider } from 'react-intl'

import '@formatjs/intl-getcanonicallocales/polyfill.js'
import '@formatjs/intl-locale/polyfill.js'
import { match } from '@formatjs/intl-localematcher'

import '@formatjs/intl-datetimeformat/add-all-tz.js' // Add ALL tz data
import '@formatjs/intl-datetimeformat/polyfill.js'
import '@formatjs/intl-numberformat/polyfill.js'
import '@formatjs/intl-pluralrules/polyfill.js'
import '@formatjs/intl-relativetimeformat/polyfill.js'

import '@formatjs/intl-datetimeformat/locale-data/de' // locale-data for de
import '@formatjs/intl-numberformat/locale-data/de' // locale-data for de
import '@formatjs/intl-pluralrules/locale-data/de' // Add locale data for de
import '@formatjs/intl-relativetimeformat/locale-data/de' // Add locale data for de

import '@formatjs/intl-datetimeformat/locale-data/en-GB' // locale-data for en-GB
import '@formatjs/intl-numberformat/locale-data/en-GB' // locale-data for en-GB
// import '@formatjs/intl-pluralrules/locale-data/en-GB' // Add locale data for en-GB // 存在しない
import '@formatjs/intl-relativetimeformat/locale-data/en-GB' // Add locale data for en-GB

import '@formatjs/intl-datetimeformat/locale-data/en' // locale-data for en
import '@formatjs/intl-numberformat/locale-data/en' // locale-data for en
import '@formatjs/intl-pluralrules/locale-data/en' // Add locale data for en
import '@formatjs/intl-relativetimeformat/locale-data/en' // Add locale data for en

import '@formatjs/intl-datetimeformat/locale-data/es' // locale-data for es
import '@formatjs/intl-numberformat/locale-data/es' // locale-data for es
import '@formatjs/intl-pluralrules/locale-data/es' // Add locale data for es
import '@formatjs/intl-relativetimeformat/locale-data/es' // Add locale data for es

import '@formatjs/intl-datetimeformat/locale-data/fr' // locale-data for fr
import '@formatjs/intl-numberformat/locale-data/fr' // locale-data for fr
import '@formatjs/intl-pluralrules/locale-data/fr' // Add locale data for fr
import '@formatjs/intl-relativetimeformat/locale-data/fr' // Add locale data for fr

import '@formatjs/intl-datetimeformat/locale-data/ja' // locale-data for ja
import '@formatjs/intl-numberformat/locale-data/ja' // locale-data for ja
import '@formatjs/intl-pluralrules/locale-data/ja' // Add locale data for ja
import '@formatjs/intl-relativetimeformat/locale-data/ja' // Add locale data for ja

import '@formatjs/intl-datetimeformat/locale-data/ko' // locale-data for ko
import '@formatjs/intl-numberformat/locale-data/ko' // locale-data for ko
import '@formatjs/intl-pluralrules/locale-data/ko' // Add locale data for ko
import '@formatjs/intl-relativetimeformat/locale-data/ko' // Add locale data for ko

import '@formatjs/intl-datetimeformat/locale-data/zh-Hant' // locale-data for zh-TW
import '@formatjs/intl-numberformat/locale-data/zh-Hant' // locale-data for zh-TW
import '@formatjs/intl-pluralrules/locale-data/zh' // Add locale data for zh-TW
import '@formatjs/intl-relativetimeformat/locale-data/zh-Hant' // Add locale data for zh-TW

import { useInitialState } from 'hooks/useInitialState'
import { useInitialStateContext } from '../../contexts/initialStateContext'

type Locale = (typeof locales)[number]
type Validate<T> = { [K in keyof T as K extends keyof typeof en ? never : K]: T[K] }

const assert = <TMessage extends Record<string, string>>(message: TMessage) =>
  message as unknown as Validate<TMessage>[keyof Validate<TMessage>] extends never
    ? TMessage
    : Validate<TMessage>[keyof Validate<TMessage>]

const locales = ['de', 'en-GB', 'en', 'es', 'fr', 'ja', 'ko', 'zh-Hant'] as const
const messages = {
  en: assert(en),
  'en-GB': assert({ ...en, ...enGb }),
  es: assert(es),
  fr: assert(fr),
  de: assert(de),
  ja: assert(ja),
  ko: assert(ko),
  'zh-Hant': assert(zhTw),
} as const satisfies Record<Locale, Record<string, string>>

// This class provides Intl context to its children.
// The children can format message with FormattedMessage component.
export const MiteneIntlProvider = (props: { children?: ReactNode | undefined }) => {
  const initialState = useInitialState('locale')
  const l = useInitialStateContext()?.locale ?? initialState

  // ブラウザから渡ってきた locale を解析しなおす (主に zh-TW を zh-Hant にする)
  const locale = useMemo<Locale>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (l === null ? 'en' : match([l], locales, 'en')) as any,
    [l],
  )
  const msg = messages[locale] || messages.en
  return <IntlProvider {...props} locale={locale} key={locale} defaultLocale='en' messages={msg} />
}
